import React, {forwardRef} from 'react';
import type {ComponentType} from 'react';
import type {TextFieldProps} from '../TextField';
import {TextField} from '../TextField';
import {IconButton, IconButtonProps} from '../Button';
import {InputAdornment} from '../Input';
import DefaultSearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {styled} from '@mui/material/styles';
import type {FuelInputVariant} from '../types/general';
import {composeEventHandlers} from '../utils/composeEventHandlers';
import {useControlled} from '@mui/material';

type BastTextFieldProps = Omit<TextFieldProps, 'endAdornment'>;

export interface SearchProps extends BastTextFieldProps {
    /**
     * The default React Icon Component used to render the the search icon.
     * @default Search
     */
    SearchIcon?: ComponentType;
    /**
     * Search variants. Sets Input variant.
     * @default 'outlined'
     */
    variant?: FuelInputVariant;
    /**
     * Event handler for onClear function.
     * @default undefined;
     */
    onClear?: () => void;
}

const StyledIconButton = styled(IconButton)<IconButtonProps>(({edge}) => ({
    height: '1.5rem',
    width: '1.5rem',

    ...edge === 'end' && {
        '&.MuiIconButton-edgeEnd': {
            marginRight: 0
        }
    }
}));

export const Search = forwardRef<HTMLDivElement, SearchProps>(({
    SearchIcon = DefaultSearchIcon, placeholder, value: valueProp, onChange,
    onClear, startAdornment, variant = 'outlined', ...props
}, ref) => {
    const [value, setValue] = useControlled({
        controlled: valueProp,
        default: '',
        name: 'Search'
    });

    const updateValue = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setValue(e.target.value);
    };

    const clearValue = (): void => {
        setValue('');
    };

    const handleChange = composeEventHandlers(updateValue, onChange);
    const handleClear = composeEventHandlers(clearValue, onClear);

    return (
        <TextField
            {...props}
            endAdornment={
                <InputAdornment position='end'>
                    {Boolean(value) && (
                        <StyledIconButton
                            aria-label='Clear'
                            className='FuelSearchInputClearButton'
                            edge='end'
                            onClick={handleClear}
                            size='small'
                            type='reset'
                        >
                            <ClearIcon/>
                        </StyledIconButton>
                    )}
                </InputAdornment>
            }
            onChange={handleChange}
            placeholder={placeholder || 'Search'}
            ref={ref}
            startAdornment={startAdornment || (
                <InputAdornment position='start'>
                    <SearchIcon/>
                </InputAdornment>
            )}
            value={value}
            variant={variant}
        />
    );
});

Search.displayName = 'Search';
